import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import { verificationsApi } from "@/api/verifications";
import { authApi } from "@/api/auth";
import { track } from "@/lib/telemetry";
import { isFlagOn } from "@/lib/flags";

// Get Verified v2 — telemetry shim. Gated on getverified_v2 flag so a
// rollback silences emission without removing call sites. Same pattern
// as Discovery v2 / BR-checkout-ack.
function trackIfEnabled(name, props) {
  if (!isFlagOn("getverified_v2")) return;
  track(name, props);
}

/**
 * GetVerifiedPage — primary CTA destination from the landing page.
 *
 * Async only at launch (founder decision, 2026-10-09): a written review with a
 * 24h / 48h / 1 week turnaround. There is no live-session path here.
 *
 * Flow:
 *   1. CATEGORY   — pick one of the domains a reviewer can take today
 *   2. PASTE      — drop the AI output + state your goal
 *   3. TURNAROUND — 24 hours / 48 hours / 1 week
 *   4. ACCOUNT    — sign in / register if needed
 *   5. CHECKOUT   — redirect to Stripe
 */

const STEP = {
  CATEGORY:    1,
  PASTE:       2,
  TURNAROUND:  3,
  ACCOUNT:     4,
  CHECKOUT:    5,
};

// Bump when STEP numbering changes, so a saved draft from an older layout is
// remapped instead of resuming on the wrong screen. v1 (no field) had a Mode
// step at 3, so Turnaround was 4, Account 5 and Checkout 6.
const DRAFT_VERSION = 2;

// Validation thresholds — match the backend Pydantic model in
// routes/verifications.py (submission_text min_length=10, user_goal=4).
const MIN_SUBMISSION_CHARS = 10;
const MIN_GOAL_CHARS       = 4;

const DEFAULT_CATEGORIES = [
  { key: "law",         label: "Law",                       icon: "⚖️" },
  { key: "product",     label: "Product",                   icon: "🎯" },
  { key: "engineering", label: "Engineering",               icon: "⚙️" },
  { key: "finance",     label: "Finance",                   icon: "💹" },
  { key: "fundraising", label: "Fundraising",               icon: "🚀" },
  { key: "accounting",  label: "Accounting & Bookkeeping",  icon: "📒" },
  { key: "strategy",    label: "Strategy Reviews",          icon: "🧭" },
  { key: "code_review", label: "Code Reviews",              icon: "💻" },
  { key: "ship_app",    label: "Ship Your Vibe-Coded App",  icon: "🚢" },
];

const DEFAULT_TURNAROUNDS = [
  { key: "24h",    label: "24 hours", price_usd: 99, hours: 24,
    sub: "Tomorrow, signed off." },
  { key: "48h",    label: "48 hours", price_usd: 69, hours: 48,
    sub: "The default." },
  { key: "1_week", label: "1 week",   price_usd: 49, hours: 168,
    sub: "Cheapest. No rush." },
];

// Progress labels shown above the step UI.
const PROGRESS_LABELS = ["Category", "Paste", "Turnaround", "Account", "Checkout"];

// ---------------------------------------------------------------------------

export default function GetVerifiedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, login, register } = useAuth();

  // Deep-link support — UseCasesPage and other entry points pass
  // ?category=<key> to skip Step 1. If the param is a known V1 category
  // we pre-select it and jump the funnel straight to Step 2 (Paste).
  // Unknown values are ignored; the user just sees the normal Category step.
  const deepLinkCategory = (() => {
    const c = (searchParams.get("category") || "").toLowerCase().trim();
    const known = new Set(DEFAULT_CATEGORIES.map((x) => x.key));
    return known.has(c) ? c : null;
  })();

  // Server-loaded copies of categories + turnarounds. The category list is the
  // server's answer (only domains a reviewer can take today); the defaults
  // below are used for labels until it arrives. `categoriesState` says whether
  // the picker may render: 'loading' | 'ready' | 'failed'.
  const [categories, setCategories]   = useState(DEFAULT_CATEGORIES);
  const [categoriesState, setCategoriesState] = useState("loading");
  const [turnarounds, setTurnarounds] = useState(DEFAULT_TURNAROUNDS);

  // If we have a deep-link category, the user has already made the Step 1
  // choice — jump them straight to Step 2.
  const [step, setStep] = useState(deepLinkCategory ? STEP.PASTE : STEP.CATEGORY);

  // Form state — persists across step transitions.
  // Deep-link wins over any draft for category — the user just clicked
  // "Submit on Law", they don't want to land in some half-finished
  // Engineering draft from yesterday.
  const [category, setCategory]               = useState(deepLinkCategory || null);
  const [submissionText, setSubmissionText]   = useState("");
  const [userGoal, setUserGoal]               = useState("");
  const [sourcePlatform, setSourcePlatform]   = useState("direct");
  const [turnaround, setTurnaround]           = useState("48h");
  // BR-008 — files attached on the Paste step. Each entry is the response
  // from POST /api/upload/artifact: {name, url, content_type, size, ext}.
  // The submit handlers pass them through to the backend, which persists
  // them in ai_context_packages.artifacts so the expert sees them in their
  // brief alongside the pasted text.
  const [attachments, setAttachments]         = useState([]);

  // BR-009 — schema-driven per-category extras (GitHub URL, jurisdiction,
  // tech stack, etc.). Each field id is a key on this object. On submit,
  // populated fields get serialized into a markdown block that lands in
  // `review_instructions` — so the expert sees them rendered nicely in
  // their brief, no backend changes needed.
  const [categoryFields, setCategoryFields]   = useState({});
  // Account state (only used at the Account step if not signed in).
  const [authMode, setAuthMode]   = useState("register"); // register | login
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [name, setName]           = useState("");

  const [submitting, setSubmitting] = useState(false);

  // P1-9: persist draft across browser restarts (localStorage) with a
  // 24h TTL. Previously this used sessionStorage which silently wiped
  // mid-flow work if the user closed the browser. The TTL prevents
  // stale drafts from haunting the user weeks later.
  //
  // CRITICAL: STEP.CHECKOUT is NEVER persisted or restored. It's a
  // transient "we're redirecting to Stripe" state — the submit handler
  // has already (a) inserted the row server-side and (b) cleared the
  // draft. If we resume at checkout, the user sees the spinner forever
  // because nothing re-fires the redirect. Always resume at TURNAROUND
  // or earlier so the user re-confirms before another payment hop.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("xpert_verify_draft");
      if (!raw) return;
      const d = JSON.parse(raw);
      const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
      if (!d._savedAt || Date.now() - d._savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem("xpert_verify_draft");
        return;
      }
      // Deep-link conflict handling:
      //   • No deep-link             → restore the draft normally
      //   • Deep-link == draft.cat   → resume the in-progress draft for
      //                                that category (user came back to
      //                                the same use case)
      //   • Deep-link != draft.cat   → user clicked a different use case;
      //                                discard the draft, start fresh in
      //                                the new category. NOT respecting
      //                                this would silently land them in
      //                                yesterday's half-written Engineering
      //                                submission when they clicked Law.
      if (deepLinkCategory && d.category && d.category !== deepLinkCategory) {
        localStorage.removeItem("xpert_verify_draft");
        return;
      }
      if (d.category)       setCategory(d.category);
      if (d.submissionText) setSubmissionText(d.submissionText);
      if (d.userGoal)       setUserGoal(d.userGoal);
      if (d.sourcePlatform) setSourcePlatform(d.sourcePlatform);
      if (d.turnaround)     setTurnaround(d.turnaround);
      // Attached files are persisted by URL only — the actual binary lives
      // in Supabase Storage already. Safe to restore even days later (until
      // the bucket's retention policy expires them, which is BR-008 V2).
      if (Array.isArray(d.attachments)) setAttachments(d.attachments);
      // BR-009: per-category extras (GitHub URL, jurisdiction, etc.).
      if (d.categoryFields && typeof d.categoryFields === "object") {
        setCategoryFields(d.categoryFields);
      }
      // Cap at < STEP.CHECKOUT (i.e., allow restoring to STEP.ACCOUNT
      // but NOT STEP.CHECKOUT). Background:
      //
      //   • STEP.CHECKOUT is transient — by the time we reach it the
      //     server-side row exists and the redirect to Stripe has been
      //     queued. Resuming there strands the user on a spinner with
      //     nothing re-firing the redirect. Original test guard in
      //     `getVerifiedDraft.test.js` enforces this; loosened cap
      //     still satisfies it (test allows < STEP.ACCOUNT OR < STEP.CHECKOUT).
      //
      //   • STEP.ACCOUNT used to be capped out for the same reason
      //     (stranding), but Get Verified v2 added the one-click Google
      //     OAuth button. Now an authed user landing at STEP.ACCOUNT
      //     immediately auto-skips to checkout via the useEffect below.
      //     The OAuth resume flow (StepAccount → Google → AuthCallback →
      //     /get-verified) RELIES on restoring to STEP.ACCOUNT — without
      //     it, the user is sent back to Step 1 (Category) and loses
      //     all the wizard context they just provided.
      //
      // When deep-linked, never resume to STEP.CATEGORY — that defeats
      // the whole point ("skip the category page"). Floor at STEP.PASTE.
      // A draft saved before the Mode step was removed has no version and the
      // old numbering (Mode 3, Turnaround 4, Account 5, Checkout 6). Remap it:
      // Mode and Turnaround both resume at Turnaround, Account at Account, and
      // anything later is dropped. (A draft that chose the old live-session
      // mode lands on Turnaround too; there is no live path any more.)
      let savedStep = d.step;
      if (d.v !== DRAFT_VERSION && savedStep > 2) {
        if (savedStep <= 4)       savedStep = STEP.TURNAROUND;
        else if (savedStep === 5) savedStep = STEP.ACCOUNT;
        else                      savedStep = 0;
      }
      if (savedStep && savedStep < STEP.CHECKOUT) {
        const floor = deepLinkCategory ? STEP.PASTE : STEP.CATEGORY;
        const restoredStep = Math.max(savedStep, floor);
        setStep(restoredStep);
        // Telemetry: signal that a draft was successfully restored so we
        // can measure "users who came back" vs "fresh sessions". We log
        // the resumed step and (relative) age — never the draft contents.
        trackIfEnabled("getverified_resumed_from_draft", {
          restored_step:    restoredStep,
          hours_since_save: Math.round((Date.now() - d._savedAt) / 3_600_000),
        });
        // Mark that this mount was a resume so the getverified_started
        // event (fired in a separate useEffect below) carries the flag.
        resumedRef.current = true;
      }
    } catch (_) { /* noop */ }
    // Intentional fire-once-on-mount: depending on `deepLinkCategory`
    // would re-run the restore on every search-param change and could
    // clobber in-progress wizard state. The dep is captured at mount
    // time, which is exactly what we want for draft restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marker used by the started-event effect to know whether this mount
  // was a fresh session or a resume. Set inside the restore useEffect
  // above. A ref (not state) because we don't want re-renders, and the
  // started event fires synchronously in a follow-on effect that needs
  // the value but doesn't need to react to its change.
  const resumedRef = useRef(false);

  // Fire `getverified_started` exactly once per mount. Runs after the
  // restore effect above so `resumedRef` is populated by the time we
  // emit. Placing this in its own useEffect (rather than inside the
  // restore effect) keeps the "did a restore happen?" logic separate
  // from the "did the funnel start?" logic — clearer when one changes
  // independently.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackIfEnabled("getverified_started", {
      has_deep_link_category: !!deepLinkCategory,
      resumed:                resumedRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on every change (with TTL timestamp).
  // We DON'T persist step=CHECKOUT — by the time we reach it, the server-
  // side row already exists; persisting just creates a foot-gun on resume.
  useEffect(() => {
    if (step === STEP.CHECKOUT) return;  // transient — see comment above
    try {
      localStorage.setItem("xpert_verify_draft", JSON.stringify({
        v: DRAFT_VERSION,
        category, submissionText, userGoal, sourcePlatform,
        turnaround,
        attachments, categoryFields,
        step,
        _savedAt: Date.now(),
      }));
    } catch (_) { /* storage full — non-critical */ }
  }, [category, submissionText, userGoal, sourcePlatform,
      turnaround, attachments, categoryFields, step]);

  // Hydrate categories/turnarounds from server (fire-and-forget).
  useEffect(() => {
    let mounted = true;
    verificationsApi.getCategories()
      .then((data) => {
        if (!mounted) return;
        // F10: the server lists only categories a reviewer can take right
        // now. Trust an explicit answer, even an empty one, and drop a
        // pre-selected (deep-linked / drafted) category it no longer offers.
        if (Array.isArray(data?.categories)) {
          setCategories(data.categories);
          setCategoriesState("ready");
          const offered = new Set(data.categories.map((c) => c.key));
          setCategory((cur) => {
            if (cur && !offered.has(cur)) {
              setStep(STEP.CATEGORY);
              return null;
            }
            return cur;
          });
        }
        if (data?.turnarounds?.length) setTurnarounds(data.turnarounds);
        // A malformed answer is treated like a failed one: offer nothing.
        if (!Array.isArray(data?.categories)) setCategoriesState("failed");
      })
      .catch(() => {
        // Turnaround defaults suffice, but a category list we could not load
        // is not guessed: offering a domain nobody can review would take money
        // for a review nobody can deliver (same fail-closed rule as the API).
        if (mounted) setCategoriesState("failed");
      });
    return () => { mounted = false; };
  }, []);

  // If the user lands on the Account step already authenticated, skip past it.
  useEffect(() => {
    if (step === STEP.ACCOUNT && user) {
      submitVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, user]);

  // -------------------------------------------------------------------------
  // Step navigation helpers
  // -------------------------------------------------------------------------

  const submissionLen  = submissionText.trim().length;
  const goalLen        = userGoal.trim().length;
  const needsMoreText  = submissionLen < MIN_SUBMISSION_CHARS;
  const needsMoreGoal  = goalLen < MIN_GOAL_CHARS;
  const canAdvanceFromPaste = !needsMoreText && !needsMoreGoal;

  const goToStep = (next) => {
    // Telemetry: distinguish forward / back navigation so funnel queries
    // can compute "users who went back vs. abandoned outright". Using
    // the current `step` closure value here is intentional — we want the
    // pre-transition value, not the post.
    if (next > step) {
      trackIfEnabled("getverified_step_advanced", {
        from: step, to: next, mode: "async",
      });
    } else if (next < step) {
      trackIfEnabled("getverified_step_back", { from: step, to: next });
    }
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // -------------------------------------------------------------------------
  // Submission
  // -------------------------------------------------------------------------

  const submitVerification = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const reviewInstructions = serializeCategoryFields(category, categoryFields);

      // Async review: POST /api/verifications
      const resp = await verificationsApi.create({
        category,
        user_goal:        userGoal.trim(),
        submission_text:  submissionText.trim(),
        artifact_name:    `${labelForCategory(category, categories)} review`,
        turnaround,
        source_platform:  sourcePlatform || "direct",
        review_instructions: reviewInstructions || undefined,
        // BR-008: attached files are persisted with the context package.
        artifacts:        attachments.map(a => ({
          name:         a.name,
          url:          a.url,
          content_type: a.content_type,
          size:         a.size,
          ext:          a.ext,
        })),
      });
      const result = resp;
      const nextUrl = result?.checkout_url || `/review/${result.id}/checkout`;
      toast.success("Review submitted. Redirecting to checkout…");

      // Telemetry: successful submission → about to redirect. Fire BEFORE
      // the localStorage clear so the event makes it out even if the
      // browser interrupts the redirect. `mode` stays in the payload (always
      // "async") so existing funnel queries keep working.
      trackIfEnabled("getverified_submitted", {
        mode: "async",
        category,
        turnaround_key: turnaround,
      });

      // Clear the draft — submission is server-side now.
      localStorage.removeItem("xpert_verify_draft");
      goToStep(STEP.CHECKOUT);

      // Give the success toast a beat, then hand off to Stripe / review-checkout.
      setTimeout(() => { window.location.href = nextUrl; }, 800);
    } catch (err) {
      // Friendlier error messages. Common failures:
      //   • 401  session expired
      //   • 502  Stripe couldn't open a session
      //   • 422  validation error from Pydantic
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "Submission failed";
      if (status === 401) {
        toast.error("Session expired. Please sign in again.");
      } else if (status === 502) {
        toast.error("Couldn't reach the payment processor. Please try again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Account step — auth flow
  // -------------------------------------------------------------------------

  const handleAuthAndSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (authMode === "login") {
        await login(email.trim(), password);
      } else {
        await register(email.trim(), password, name.trim() || email.trim());
      }
      // submitVerification will be triggered by the useEffect once user appears.
      await submitVerification();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Authentication failed";
      toast.error(typeof detail === "string" ? detail : "Authentication failed");
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      {/* Top bar */}
      <header className="border-b border-slateline-200 bg-canvas/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-display text-xl text-ink">xpert<span className="text-brand-green">.</span></span>
          </Link>
          <div className="flex items-center gap-4">
            {/* SignInLink — the early-auth lever. Returning users who
                click this sign in BEFORE reaching STEP.ACCOUNT, which
                triggers the auto-skip useEffect (~line 232) and lands
                them directly on checkout. Hidden once authed since
                there's nothing to do. Tracked so we can measure how
                often the lever is actually pulled. */}
            {!user && step < STEP.ACCOUNT && (
              <SignInLink
                fromStep={step}
                onClick={() => {
                  trackIfEnabled("getverified_signin_link_clicked", {
                    from_step: step,
                  });
                }}
              />
            )}
            <div className="text-xs text-slateline-400 tracking-wider uppercase">
              Get Verified
            </div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <ProgressBar step={step} />
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {step === STEP.CATEGORY && (
          <StepCategory
            categories={categories}
            categoriesState={categoriesState}
            selected={category}
            onSelect={(k) => {
              setCategory(k);
              goToStep(STEP.PASTE);
            }}
          />
        )}

        {step === STEP.PASTE && (
          <StepPaste
            category={category}
            submissionText={submissionText}
            setSubmissionText={setSubmissionText}
            userGoal={userGoal}
            setUserGoal={setUserGoal}
            sourcePlatform={sourcePlatform}
            setSourcePlatform={setSourcePlatform}
            attachments={attachments}
            setAttachments={setAttachments}
            categoryFields={categoryFields}
            setCategoryFields={setCategoryFields}
            onBack={() => goToStep(STEP.CATEGORY)}
            onNext={() => goToStep(STEP.TURNAROUND)}
            canAdvance={canAdvanceFromPaste}
            submissionLen={submissionLen}
            goalLen={goalLen}
            needsMoreText={needsMoreText}
            needsMoreGoal={needsMoreGoal}
          />
        )}

        {/* Step 3: turnaround */}
        {step === STEP.TURNAROUND && (
          <StepTurnaround
            turnarounds={turnarounds}
            selected={turnaround}
            onSelect={(k) => setTurnaround(k)}
            onBack={() => goToStep(STEP.PASTE)}
            onNext={() => {
              if (user) submitVerification();
              else goToStep(STEP.ACCOUNT);
            }}
          />
        )}

        {/* Render gated on !authLoading too: when resuming from a Google
            OAuth round-trip (StepAccount → Google → AuthCallback →
            /get-verified with draft restoring to STEP.ACCOUNT), `user` is
            briefly null while fetchUser() runs. Without this guard,
            StepAccount would flash for a fraction of a second before the
            auto-skip useEffect fires. Showing nothing during the brief
            auth-loading state is the cleaner UX. */}
        {step === STEP.ACCOUNT && !user && !authLoading && (
          <StepAccount
            authMode={authMode} setAuthMode={setAuthMode}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            name={name} setName={setName}
            submitting={submitting || authLoading}
            onSubmit={handleAuthAndSubmit}
            onBack={() => goToStep(STEP.TURNAROUND)}
            onMethodChosen={(method) =>
              trackIfEnabled("getverified_account_method_chosen", { method })
            }
            priceLabel={priceLabel(turnaround, turnarounds)}
          />
        )}

        {step === STEP.CHECKOUT && (
          <StepCheckoutPending
            priceLabel={priceLabel(turnaround, turnarounds)}
          />
        )}
      </main>
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

/**
 * SignInLink — small "Already have an account? Sign in" affordance shown
 * in the wizard header on every pre-account step. The key conversion
 * lever: a returning user who clicks this sails through to checkout
 * because the existing auto-skip useEffect detects them as authed when
 * they reach STEP.ACCOUNT.
 *
 * Routes to /login?return_to=/get-verified so the draft restores when
 * they come back. The auth round-trip itself is owned by LoginPage —
 * we don't reimplement the form here; just send users there.
 */
function SignInLink({ fromStep, onClick }) {
  return (
    <Link
      to="/login?return_to=/get-verified"
      onClick={onClick}
      data-from-step={fromStep}
      className="text-xs text-brand-green hover:underline font-semibold tracking-wide"
    >
      Already have an account? Sign in →
    </Link>
  );
}

function ProgressBar({ step }) {
  const labels = PROGRESS_LABELS;
  const total = labels.length;
  return (
    <div>
      <div className="flex items-center gap-2">
        {labels.map((label, idx) => {
          const i = idx + 1;
          const active   = i === step;
          const complete = i <  step;
          return (
            <div key={label} className="flex-1 flex items-center gap-2">
              <div
                className={[
                  "h-1 rounded-full flex-1 transition-all duration-300",
                  complete ? "bg-brand-green"
                    : active ? "bg-brand-green/70"
                    : "bg-slateline-200",
                ].join(" ")}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-slateline-500 tracking-wider uppercase">
        <span>Step {step} of {total}</span>
        <span>{labels[step - 1]}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepCategory — domain picker
// ---------------------------------------------------------------------------
//
// The server decides which domains are offered (GET /verifications/categories
// lists only those a reviewer can take today), so this step shows exactly that
// list: no per-domain expert counts, no "Most experts" or "Coming soon" badges.
// The 2026-10-09 live copy review found invented counts here ("4 experts" on
// domains that were really covered by one reviewer).
//
// We deliberately don't lean on hover-only reveals (mobile users can't hover)
// — the hint copy is always visible.

const CATEGORY_META = {
  law:         { hint: "Contracts, ToS, IP, employment, indemnification"  },
  product:     { hint: "PRDs, positioning, GTM, roadmaps, user research"  },
  engineering: { hint: "Architecture, system design, infra, security"     },
  finance:     { hint: "Models, valuations, unit economics, runway"       },
  fundraising: { hint: "Pitch decks, investor updates, term sheets"       },
  accounting:  { hint: "Books, tax filings, audit prep, controller QC"    },
  strategy:    { hint: "Board memos, market entry, business plans"        },
  code_review: { hint: "Pull requests, refactors, performance, security"  },
  ship_app:    { hint: "Production readiness for AI-built apps"           },
};

function StepCategory({ categories, categoriesState, selected, onSelect }) {
  const ready = categoriesState === "ready";

  return (
    <section>
      {/* Header */}
      <div className="mb-7">
        <p className="text-xs text-brand-green tracking-[0.2em] uppercase font-semibold mb-3">
          Step 1
        </p>
        <h1 className="font-display text-3xl sm:text-5xl text-ink leading-[1.05]">
          Where does your AI output need a{" "}
          <span className="text-brand-green italic">human signoff</span>?
        </h1>
        <p className="text-slateline-600 mt-3 text-sm sm:text-base max-w-xl">
          Pick the domain. Ankit Kumar, Xpert's founder, reviews it and writes
          you a verdict.
        </p>
        {/* Friction-reduction reassurance — addresses the "wait, am I
            committing to an account now?" worry that drops users at
            step 1 before they've engaged with the wizard. Keeps it
            calm: progress saves locally, account is one click, late. */}
        <p className="text-xs text-slateline-500 mt-3 max-w-xl">
          Your progress saves automatically. We'll only ask for an account
          at the end — Google works in one click.
        </p>
      </div>

      {/* Trust ribbon — only renders once the server has answered: how many
          domains are open and the starting price. No expert count (2026-10-09). */}
      {ready && categories.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slateline-500
                        border-y border-slateline-100 py-2.5">
          <span>{categories.length} {categories.length === 1 ? "domain" : "domains"} open today</span>
          <span>Written review from <strong className="text-ink">$49</strong></span>
        </div>
      )}

      {categoriesState === "loading" && (
        <p className="mb-6 text-sm text-slateline-500" role="status">Loading the domains open today…</p>
      )}

      {categoriesState === "failed" && (
        <p className="mb-6 text-sm text-slateline-600 border border-slateline-200 rounded-md p-4 bg-canvas-card">
          We couldn't load the domains open for review. Please refresh the
          page, or try again in a few minutes.
        </p>
      )}

      {ready && categories.length === 0 && (
        <p className="mb-6 text-sm text-slateline-600 border border-slateline-200 rounded-md p-4 bg-canvas-card">
          No reviewer is taking new requests right now, so there is nothing to
          pay for. Please check back soon.
        </p>
      )}

      {/* Category grid. One card per category the server says a reviewer can take (F10). */}
      {ready && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((c, idx) => {
            const meta       = CATEGORY_META[c.key] || {};
            const isSelected = selected === c.key;

            return (
              <button
                key={c.key}
                onClick={() => onSelect(c.key)}
                style={{ animationDelay: `${idx * 35}ms` }}
                className={[
                  "group relative text-left p-5 sm:p-6 rounded-lg border transition-all",
                  "animate-[fadeInUp_0.4s_ease-out_both]",
                  "hover:border-ink/40 hover:-translate-y-[2px] hover:shadow-md",
                  isSelected
                    ? "border-ink bg-brand-greenPale/40 ring-1 ring-brand-green/40 shadow-sm"
                    : "border-slateline-200 bg-canvas-card",
                ].join(" ")}
              >
                {/* Icon */}
                <div className="text-[28px] mb-2 leading-none">{c.icon || "✦"}</div>

                {/* Label */}
                <div className="font-display text-lg sm:text-xl text-ink leading-tight">
                  {c.label}
                </div>

                {/* Hint copy */}
                {meta.hint && (
                  <p className="text-[12.5px] text-slateline-500 mt-1.5 leading-snug line-clamp-2">
                    {meta.hint}
                  </p>
                )}

                {/* Bottom meta row: format + starting price */}
                <div className="mt-4 flex items-center justify-between text-[11px]
                                text-slateline-500 border-t border-slateline-100 pt-3">
                  <span>Written review</span>
                  <span className="text-ink/80 font-semibold">from $49</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Reassurance line. Lowers the cost of picking — addresses the
          "what if I pick wrong" hesitation. */}
      <p className="mt-6 text-center text-[12px] text-slateline-400">
        Not sure which domain? Pick the closest — you can switch any time before paying.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// BR-009 — schema-driven per-category extras on the Paste step
// ---------------------------------------------------------------------------
//
// Each category gets a tailored intake form. The schema below is the source
// of truth — adding a new category = adding one entry. The renderer reads
// the schema and produces the form rows. No JSX-per-category sprawl.
//
// Fields populated by the user are serialized into a markdown block on
// submit (see serializeCategoryFields below) and sent as the existing
// `review_instructions` field. The expert sees them rendered nicely in
// their brief. No backend / migration changes needed.

const PASTE_SCHEMAS = {
  // ─── CODE REVIEW ───────────────────────────────────────────────────────
  code_review: {
    headline:    "Show us the code.",
    sub:         "GitHub link, code paste, screenshots — give the reviewer what they need to dig in.",
    aiOutputLabel:       "Code / AI conversation",
    aiOutputPlaceholder: "Paste a code snippet, error trace, or the AI conversation that produced this code…",
    goalLabel:           "What should the reviewer focus on?",
    goalPlaceholder:     "e.g. Security · Performance · Production readiness · Code quality · Architecture",
    fields: [
      { id: "github_url", label: "GitHub link",       type: "url",
        placeholder: "https://github.com/you/repo · or a PR / branch / commit URL" },
      { id: "tech_stack", label: "Tech stack",        type: "tags",
        suggestions: ["TypeScript", "Python", "Go", "Rust", "Ruby", "Java", "Swift",
                      "React", "Next.js", "Vue", "Svelte", "FastAPI", "Django", "Rails",
                      "Postgres", "MongoDB", "Redis", "AWS", "GCP", "Vercel", "Fly.io"] },
      { id: "ai_coding_tool", label: "Which AI tool built this?", type: "pill-select",
        options: ["Cursor", "Claude Code", "GitHub Copilot", "Cline", "Continue",
                  "v0", "Bolt", "Lovable", "Replit Agent", "Other"] },
      { id: "live_url",   label: "Live URL (optional)", type: "url",
        placeholder: "https://yourapp.com — if the reviewer should try it" },
      { id: "access_notes", label: "Access notes (optional)", type: "textarea",
        rows: 3,
        placeholder: "Login creds, env setup, demo data — anything the reviewer needs to actually run this. We pass these securely.",
        sensitive: true },
    ],
  },

  // ─── LAW ──────────────────────────────────────────────────────────────
  law: {
    headline:    "Get the legal text reviewed.",
    sub:         "Upload the document or paste the draft — say which jurisdiction it applies to.",
    aiOutputLabel:       "Clause, draft, or AI-generated text",
    aiOutputPlaceholder: "Paste the contract clause, NDA text, or AI-generated terms — or describe the situation if you're attaching the doc above.",
    goalLabel:           "What do you want the reviewer to check?",
    goalPlaceholder:     "e.g. Is this enforceable in California? · Is the IP assignment broad enough? · Are these terms standard?",
    tipBanner: "Best for law: upload the actual document (PDF / DOCX) below — pasted text often loses formatting.",
    fields: [
      { id: "doc_type",     label: "Document type", type: "select", required: true,
        options: ["NDA", "Master Services Agreement", "Terms of Service",
                  "Privacy Policy", "Employment Agreement", "Contractor Agreement",
                  "IP Assignment", "Investor Agreement (SAFE / Note / Term Sheet)",
                  "Lease / Rental", "Cease & Desist Response", "Other"] },
      { id: "jurisdiction", label: "Governing jurisdiction", type: "select", required: true,
        options: ["US — California", "US — Delaware", "US — New York", "US — Texas", "US — Other",
                  "UK", "EU", "India", "Singapore", "Canada", "Australia", "Other / Not sure"] },
      { id: "counterparty", label: "Who's on the other side?", type: "pill-select",
        options: ["Customer / Client", "Vendor / Supplier", "Employee",
                  "Contractor", "Investor", "Cofounder", "Landlord", "Other"] },
    ],
  },

  // ─── SHIP YOUR VIBE-CODED APP ─────────────────────────────────────────
  ship_app: {
    headline:    "Production-ready check for your vibe-coded app.",
    sub:         "Live URL + GitHub + access. The reviewer will exercise it and write up what's left to ship.",
    aiOutputLabel:       "What did you build?",
    aiOutputPlaceholder: "Paste the original prompt, the AI conversation, or a summary of what the AI built for you.",
    goalLabel:           "What's the gap to ship?",
    goalPlaceholder:     "e.g. Security · Performance · UX polish · Test coverage · Deploy readiness · Auth & RBAC",
    fields: [
      { id: "live_url",   label: "Live URL", type: "url", required: true,
        placeholder: "https://yourapp.com (or a staging URL the reviewer can hit)" },
      { id: "github_url", label: "GitHub link", type: "url",
        placeholder: "https://github.com/you/repo" },
      { id: "tech_stack", label: "Tech stack", type: "tags",
        suggestions: ["TypeScript", "React", "Next.js", "Vue", "Svelte", "Python",
                      "FastAPI", "Django", "Node", "Express", "Postgres", "Supabase",
                      "Firebase", "Vercel", "Fly.io", "Railway", "Tailwind", "shadcn/ui"] },
      { id: "ai_coding_tool", label: "Built primarily with", type: "pill-select",
        options: ["Cursor", "Claude Code", "v0", "Bolt", "Lovable", "Replit Agent",
                  "GitHub Copilot", "Other"] },
      { id: "known_issues", label: "Known issues (optional)", type: "textarea", rows: 3,
        placeholder: "Anything you already know is broken or scary. Helps the reviewer prioritise." },
      { id: "access_notes", label: "Access notes (optional)", type: "textarea", rows: 3,
        sensitive: true,
        placeholder: "Test creds, demo accounts, env vars the reviewer needs to actually run this. Shared securely." },
    ],
  },

  // ─── PRODUCT ──────────────────────────────────────────────────────────
  product: {
    headline:    "Sharpen your product thinking.",
    sub:         "Drop the PRD, positioning, or roadmap text. Ankit Kumar, Xpert's founder, reads it and writes you a verdict.",
    aiOutputLabel:       "PRD / positioning / strategy text",
    aiOutputPlaceholder: "Paste the AI-drafted PRD, positioning statement, GTM plan, or roadmap.",
    goalLabel:           "What should the reviewer pressure-test?",
    goalPlaceholder:     "e.g. Is this differentiated enough? · Are the metrics right? · What's missing?",
    fields: [
      { id: "product_stage", label: "Product stage", type: "pill-select",
        options: ["Idea", "Prototype", "Private beta", "Public beta", "GA / live", "Growth stage"] },
      { id: "target_user", label: "Who's the target user?", type: "text",
        placeholder: "e.g. Solo founders shipping AI-built apps · CFOs at Series A SaaS" },
    ],
  },

  // ─── FINANCE ──────────────────────────────────────────────────────────
  finance: {
    headline:    "Get the numbers checked.",
    sub:         "Upload the model or paste the spreadsheet rows. A reviewer reads it and writes you a verdict.",
    aiOutputLabel:       "Model summary / spreadsheet / AI-drafted analysis",
    aiOutputPlaceholder: "Paste the assumptions, calculations, or AI-drafted analysis.",
    goalLabel:           "What should the reviewer verify?",
    goalPlaceholder:     "e.g. Is the runway calc right? · Are the unit economics realistic? · Valuation sanity check.",
    fields: [
      { id: "company_stage", label: "Company stage", type: "pill-select",
        options: ["Pre-seed", "Seed", "Series A", "Series B+", "Bootstrapped / profitable", "Personal / pre-company"] },
      { id: "currency", label: "Currency", type: "pill-select",
        options: ["USD", "EUR", "GBP", "INR", "Other"] },
    ],
  },

  // ─── FUNDRAISING ──────────────────────────────────────────────────────
  fundraising: {
    headline:    "Get your pitch read before you send it.",
    sub:         "Deck text, fundraising email, investor update — paste it and say what feedback you want.",
    aiOutputLabel:       "Pitch text / deck content / fundraising email",
    aiOutputPlaceholder: "Paste the slide content, the email body, or the AI-drafted story.",
    goalLabel:           "What feedback do you want?",
    goalPlaceholder:     "e.g. Is the narrative clear? · Will this email get a reply? · What's the obvious VC pushback?",
    fields: [
      { id: "round_stage", label: "Round", type: "pill-select",
        options: ["Pre-seed", "Seed", "Series A", "Series B+", "Bridge / extension"] },
      { id: "amount_target", label: "Amount targeting (optional)", type: "text",
        placeholder: "e.g. $500k SAFE · $2M seed · $10M Series A" },
      { id: "doc_type", label: "What is this?", type: "pill-select",
        options: ["Pitch deck", "Fundraising email", "Investor update", "Term sheet response", "Founder bio / story", "Memo"] },
    ],
  },

  // ─── ACCOUNTING ──────────────────────────────────────────────────────
  accounting: {
    headline:    "Books / tax / audit — get a second read.",
    sub:         "Upload the file or paste the key entries. A reviewer reads it and writes you a verdict. It is not a filing or a sign-off.",
    aiOutputLabel:       "Entries / AI-drafted analysis",
    aiOutputPlaceholder: "Paste journal entries, AI-drafted notes, or describe the situation.",
    goalLabel:           "What's the goal?",
    goalPlaceholder:     "e.g. Tax filing QC · Audit prep · Books cleanup · Controller review.",
    fields: [
      { id: "country", label: "Country / jurisdiction", type: "select", required: true,
        options: ["US — Federal", "US — California", "US — Delaware", "US — Other state",
                  "UK", "EU", "India", "Canada", "Australia", "Singapore", "Other"] },
      { id: "period", label: "Period", type: "text", placeholder: "e.g. FY24 · Q2 2026 · YTD" },
    ],
  },

  // ─── STRATEGY ────────────────────────────────────────────────────────
  strategy: {
    headline:    "Stress-test your strategy.",
    sub:         "Board memo, market entry plan, business case — drop the draft and tell us what's at stake.",
    aiOutputLabel:       "Strategy document or AI-drafted analysis",
    aiOutputPlaceholder: "Paste the memo, plan, or analysis.",
    goalLabel:           "What decision are you trying to make?",
    goalPlaceholder:     "e.g. Should we enter Europe? · Pivot vs. extension? · Acquire or build?",
    fields: [
      { id: "doc_type", label: "What is this?", type: "pill-select",
        options: ["Board memo", "Market entry plan", "Business plan", "Strategic option memo",
                  "Pivot proposal", "Other"] },
      { id: "audience", label: "Audience", type: "pill-select",
        options: ["Board", "Leadership team", "External investors", "Internal only"] },
    ],
  },

  // ─── ENGINEERING ─────────────────────────────────────────────────────
  engineering: {
    headline:    "Architecture / system / infra review.",
    sub:         "Diagram, design doc, infra config — drop what you have. A reviewer reads it and writes you a verdict.",
    aiOutputLabel:       "Design / architecture / AI-drafted text",
    aiOutputPlaceholder: "Paste the architecture text, design doc, or AI conversation.",
    goalLabel:           "What's the question?",
    goalPlaceholder:     "e.g. Will this scale to 10x? · Where are the failure modes? · Security review.",
    fields: [
      { id: "project_type", label: "Type", type: "pill-select",
        options: ["Architecture review", "System design", "Infra / cloud", "Security audit", "Database design"] },
      { id: "tech_stack", label: "Tech stack", type: "tags",
        suggestions: ["AWS", "GCP", "Azure", "Kubernetes", "Postgres", "Redis", "Kafka",
                      "TypeScript", "Python", "Go", "Java", "Rust"] },
      { id: "scale", label: "Scale (optional)", type: "text",
        placeholder: "e.g. 10k DAU · 5k req/s peak · 2 TB data" },
    ],
  },
};

// Generic schema for any category not above — keeps the funnel working
// even for "Coming soon" placeholders if they're ever flipped live.
const GENERIC_PASTE_SCHEMA = {
  headline:    "Show your work.",
  sub:         "Drop whatever the AI gave you — the messier, the better. The reviewer sees this verbatim.",
  aiOutputLabel:       "AI output",
  aiOutputPlaceholder: "Paste your AI-generated text here…",
  goalLabel:           "What do you want the reviewer to verify?",
  goalPlaceholder:     "e.g. Is this analysis sound? · What's missing?",
  fields: [],
};

function getSchemaForCategory(catKey) {
  return PASTE_SCHEMAS[catKey] || GENERIC_PASTE_SCHEMA;
}

// Serialize populated categoryFields into a markdown block the expert will
// see in their brief (rendered from `review_instructions`). Empty fields are
// dropped so the block stays tight. Returns "" if nothing is populated.
function serializeCategoryFields(catKey, fields) {
  if (!fields || Object.keys(fields).length === 0) return "";
  const schema = getSchemaForCategory(catKey);
  const lines = [];
  for (const f of schema.fields) {
    const v = fields[f.id];
    if (v == null || v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    const label = f.label.replace(/\s*\(optional\)\s*$/i, "");
    const value = Array.isArray(v) ? v.join(", ") : String(v).trim();
    if (!value) continue;
    // Sensitive fields (creds, access notes) get a clear tag so the expert
    // knows to keep them private and discard after the engagement.
    const sensitiveSuffix = f.sensitive ? "   *(sensitive — handle accordingly)*" : "";
    lines.push(`**${label}:**${sensitiveSuffix}\n${value}`);
  }
  if (!lines.length) return "";
  return `## ${schema.headline}\n\n${lines.join("\n\n")}`;
}


// ---------------------------------------------------------------------------
// DynamicField — renders one field from a category schema
// ---------------------------------------------------------------------------
//
// Supported types:
//   • text         — single-line text input
//   • url          — single-line input, type="url"
//   • textarea     — multi-line, rows configurable
//   • pill-select  — row of round chip buttons, single-select
//   • select       — native <select> dropdown for long option lists
//   • tags         — chip multi-select with type-to-add + suggestion chips
//
// Each field reads from / writes to the parent's `categoryFields` object
// keyed by `field.id`. No internal state — the parent is the single source
// of truth, which keeps draft persistence trivial.

function DynamicField({ field, value, onChange }) {
  if (field.type === "text" || field.type === "url") {
    return (
      <div>
        <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
          {field.label} {field.required && <span className="text-rose-500 normal-case">*</span>}
        </label>
        <input
          type={field.type === "url" ? "url" : "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="w-full mt-2 h-11 bg-white border border-slateline-200 focus:border-brand-green
                     focus:ring-2 focus:ring-brand-green/20 rounded-md px-4 text-ink
                     placeholder-slateline-400 outline-none text-sm"
        />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
          {field.label} {field.required && <span className="text-rose-500 normal-case">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={field.rows || 3}
          className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green
                     focus:ring-2 focus:ring-brand-green/20 rounded-md px-4 py-3 text-ink
                     placeholder-slateline-400 outline-none text-sm"
        />
        {field.sensitive && (
          <p className="text-[11px] text-slateline-400 mt-1.5 italic">
            🔒 Treated as sensitive — only your assigned expert can read this.
          </p>
        )}
      </div>
    );
  }

  if (field.type === "pill-select") {
    return (
      <div>
        <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
          {field.label} {field.required && <span className="text-rose-500 normal-case">*</span>}
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {field.options.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(active ? "" : opt)}
                className={[
                  "px-3 py-1.5 rounded-full text-sm border transition-all",
                  active
                    ? "border-ink bg-ink text-white"
                    : "border-slateline-300 text-slateline-600 hover:border-ink/40 hover:text-ink",
                ].join(" ")}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
          {field.label} {field.required && <span className="text-rose-500 normal-case">*</span>}
        </label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-2 h-11 bg-white border border-slateline-200 focus:border-brand-green
                     focus:ring-2 focus:ring-brand-green/20 rounded-md px-3 text-ink
                     outline-none text-sm"
        >
          <option value="">Select…</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "tags") {
    return <TagsField field={field} value={value || []} onChange={onChange} />;
  }

  return null;
}


// Multi-select chip input with type-to-add and clickable suggestion chips.
// Stores values as a string[] in the parent state.
function TagsField({ field, value, onChange }) {
  const [draft, setDraft] = useState("");

  const add = (tag) => {
    const t = tag.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  const remove = (tag) => onChange(value.filter((t) => t !== tag));

  const suggestionsToShow = (field.suggestions || []).filter((s) => !value.includes(s)).slice(0, 12);

  return (
    <div>
      <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
        {field.label} {field.required && <span className="text-rose-500 normal-case">*</span>}
      </label>

      {/* Selected chips */}
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((t) => (
            <span key={t}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1
                             bg-ink text-white text-xs rounded-full font-medium">
              {t}
              <button
                type="button"
                onClick={() => remove(t)}
                aria-label={`Remove ${t}`}
                className="text-white/70 hover:text-white text-sm leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Type-to-add input */}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          } else if (e.key === "Backspace" && !draft && value.length) {
            // Backspace on empty input removes the last tag — standard chip-input UX.
            remove(value[value.length - 1]);
          }
        }}
        placeholder={field.placeholder || "Type and press Enter to add…"}
        className="w-full mt-2 h-11 bg-white border border-slateline-200 focus:border-brand-green
                   focus:ring-2 focus:ring-brand-green/20 rounded-md px-4 text-ink
                   placeholder-slateline-400 outline-none text-sm"
      />

      {/* Suggestion chips */}
      {suggestionsToShow.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestionsToShow.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="px-2 py-1 text-[11px] border border-dashed border-slateline-300
                         rounded-full text-slateline-500 hover:border-ink/40 hover:text-ink
                         transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// StepPaste — schema-driven Step 2
// ---------------------------------------------------------------------------

function StepPaste({
  category,
  submissionText, setSubmissionText,
  userGoal, setUserGoal,
  sourcePlatform, setSourcePlatform,
  attachments, setAttachments,
  categoryFields, setCategoryFields,
  onBack, onNext, canAdvance,
  submissionLen, goalLen,
  needsMoreText, needsMoreGoal,
}) {
  const schema = getSchemaForCategory(category);

  const updateField = (id, val) => {
    setCategoryFields((prev) => ({ ...prev, [id]: val }));
  };

  return (
    <section>
      {/* Dynamic header — changes by category. The kicker stays "Step 2"
          so the user's mental progress map doesn't shift. */}
      <div className="mb-6">
        <p className="text-xs text-brand-green tracking-[0.2em] uppercase font-semibold mb-3">
          Step 2
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-ink leading-tight">
          {schema.headline}
        </h1>
        <p className="text-slateline-600 mt-3 text-sm sm:text-base max-w-2xl">
          {schema.sub}
        </p>
      </div>

      {/* Optional tip banner per category (e.g. Law nudges PDF upload) */}
      {schema.tipBanner && (
        <div className="mb-5 px-4 py-3 bg-brand-greenPale/50 border border-brand-green/30
                        rounded-md text-sm text-ink">
          <span className="text-brand-green mr-1.5">💡</span>
          {schema.tipBanner}
        </div>
      )}

      <div className="space-y-5">
        {/* CATEGORY-SPECIFIC FIELDS (rendered first so the form feels tailored) */}
        {schema.fields.length > 0 && (
          <div className="space-y-5 pb-2">
            {schema.fields.map((f) => (
              <DynamicField
                key={f.id}
                field={f}
                value={categoryFields[f.id]}
                onChange={(v) => updateField(f.id, v)}
              />
            ))}
          </div>
        )}

        {/* Divider before the always-on common fields, only if there were extras */}
        {schema.fields.length > 0 && (
          <div className="pt-3 border-t border-slateline-100" />
        )}

        {/* AI OUTPUT (always required) */}
        <div>
          <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
            {schema.aiOutputLabel}
          </label>
          <textarea
            value={submissionText}
            onChange={(e) => setSubmissionText(e.target.value)}
            placeholder={schema.aiOutputPlaceholder}
            className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                       rounded-md px-4 py-3 text-ink placeholder-slateline-400
                       outline-none min-h-[220px] font-mono text-sm leading-relaxed"
          />
          <div className="mt-1.5 flex items-center justify-between">
            <span className={[
              "text-[11px]",
              needsMoreText ? "text-rose-600 font-medium" : "text-slateline-400",
            ].join(" ")}>
              {needsMoreText
                ? `${MIN_SUBMISSION_CHARS - submissionLen} more character${
                    MIN_SUBMISSION_CHARS - submissionLen === 1 ? "" : "s"
                  } needed`
                : `${submissionLen.toLocaleString()} characters`}
            </span>
            <span className="text-[11px] text-slateline-400">
              Min. {MIN_SUBMISSION_CHARS} characters
            </span>
          </div>
        </div>

        {/* GOAL (always required) */}
        <div>
          <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
            {schema.goalLabel}
          </label>
          <textarea
            value={userGoal}
            onChange={(e) => setUserGoal(e.target.value)}
            placeholder={schema.goalPlaceholder}
            className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                       rounded-md px-4 py-3 text-ink placeholder-slateline-400
                       outline-none min-h-[80px] text-sm"
          />
          {needsMoreGoal && goalLen > 0 && (
            <div className="text-[11px] text-rose-600 font-medium mt-1.5">
              {MIN_GOAL_CHARS - goalLen} more character
              {MIN_GOAL_CHARS - goalLen === 1 ? "" : "s"} needed
            </div>
          )}
        </div>

        {/* SOURCE PLATFORM (always — separate from the per-category
            "which AI tool did you build with" field) */}
        <div>
          <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
            Which AI gave you the output above?
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {["claude", "chatgpt", "gemini", "cursor", "perplexity", "direct"].map((p) => {
              const active = sourcePlatform === p;
              return (
                <button
                  key={p}
                  onClick={() => setSourcePlatform(p)}
                  className={[
                    "px-3 py-1.5 rounded-full text-sm border capitalize transition-all",
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-slateline-300 text-slateline-600 hover:border-ink/40 hover:text-ink",
                  ].join(" ")}
                >
                  {p === "direct" ? "Other / Direct" : p}
                </button>
              );
            })}
          </div>
        </div>

        {/* ATTACHMENTS */}
        <AttachmentsField
          attachments={attachments || []}
          setAttachments={setAttachments}
        />
      </div>

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!canAdvance}
        nextLabel="Continue"
        hint={
          !canAdvance
            ? needsMoreText
              ? `Paste at least ${MIN_SUBMISSION_CHARS} characters to continue`
              : needsMoreGoal
                ? `Describe what to verify (min ${MIN_GOAL_CHARS} characters)`
                : null
            : null
        }
      />
    </section>
  );
}


// ---------------------------------------------------------------------------
// AttachmentsField — drag-and-drop + click-to-upload file picker
// ---------------------------------------------------------------------------
//
// Each file is uploaded to /api/upload/artifact individually so we can show
// per-file progress + per-file errors (one file failing shouldn't kill the
// other uploads). Successful uploads get appended to the `attachments` state
// (an array of {name, url, content_type, size, ext}); the parent submit
// handler passes that array through to the backend.
//
// We render a max of 10 files (matches the backend's max_length=10 cap).
//
// Design choice: NOT a list of "save & continue" buttons per file — files
// are appended as they finish uploading. Just-in-time. Removes a step the
// user otherwise has to remember to take.

const MAX_ATTACHMENTS = 10;
const ALLOWED_EXTS_DISPLAY = ".pdf, .docx, .txt, .md, .csv, .json, " +
                             ".js, .ts, .py, .html, .css, .yaml, " +
                             ".png, .jpg, .webp, .zip";
const ALLOWED_MIME_RE = /^(application\/(pdf|msword|json|javascript|zip|x-yaml|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)|text\/.*|image\/(png|jpeg|webp|gif))$/i;

function AttachmentsField({ attachments, setAttachments }) {
  const [dragHot, setDragHot]   = useState(false);
  const [uploading, setUploading] = useState([]);   // [{tmpId, name, pct, err}]
  const inputRef = React.useRef(null);

  const remainingSlots = Math.max(0, MAX_ATTACHMENTS - (attachments.length + uploading.length));

  const onFilesPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > remainingSlots) {
      toast.error(`You can attach at most ${MAX_ATTACHMENTS} files. Skipping the extras.`);
    }
    const accepted = files.slice(0, remainingSlots);

    // Pre-flight per-file checks BEFORE we start any upload, so the user
    // sees one cohesive error toast rather than N rejections trickling in.
    const okFiles = [];
    for (const f of accepted) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`"${f.name}" is over 25 MB — skipped.`);
        continue;
      }
      const ct = f.type || "";
      if (!ALLOWED_MIME_RE.test(ct) && !ct.startsWith("text/")) {
        // Some browsers send empty MIME for niche extensions — let it through
        // and rely on the backend's allow-list to be the ground truth.
        if (ct && ct !== "") {
          toast.error(`"${f.name}" (${ct}) isn't a supported file type — skipped.`);
          continue;
        }
      }
      okFiles.push(f);
    }
    if (!okFiles.length) return;

    // Kick off all uploads in parallel. Each one mutates `uploading` for
    // progress feedback and on success appends to `attachments`.
    for (const f of okFiles) {
      const tmpId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setUploading((u) => [...u, { tmpId, name: f.name, pct: 0, err: null }]);

      const form = new FormData();
      form.append("file", f);

      axios.post(`${API}/upload/artifact`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploading((u) => u.map((x) => x.tmpId === tmpId ? { ...x, pct } : x));
        },
      })
        .then((resp) => {
          setUploading((u) => u.filter((x) => x.tmpId !== tmpId));
          setAttachments((a) => [...a, resp.data]);
          toast.success(`Attached: ${resp.data.name}`);
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || err?.message || "Upload failed.";
          setUploading((u) => u.map((x) => x.tmpId === tmpId ? { ...x, err: detail, pct: 100 } : x));
          toast.error(`Couldn't attach "${f.name}": ${detail}`);
          // Auto-clear the failed row after a few seconds.
          setTimeout(() => {
            setUploading((u) => u.filter((x) => x.tmpId !== tmpId));
          }, 4000);
        });
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragHot(false);
    onFilesPicked(e.dataTransfer?.files);
  };

  const removeAttachment = (url) => {
    setAttachments((a) => a.filter((x) => x.url !== url));
  };

  return (
    <div>
      <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
        Attach files (optional)
      </label>
      <p className="text-xs text-slateline-400 mt-1">
        Add PDFs, screenshots, code files, or any artifact the expert should review alongside your AI output. Up to {MAX_ATTACHMENTS} files, 25&nbsp;MB each.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragHot(true); }}
        onDragLeave={() => setDragHot(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        className={[
          "mt-2 cursor-pointer rounded-md border-2 border-dashed p-5 transition-colors",
          "text-center text-sm select-none",
          dragHot
            ? "border-brand-green bg-brand-greenPale/40 text-ink"
            : "border-slateline-300 bg-canvas-card text-slateline-500 hover:border-ink/40 hover:text-ink",
        ].join(" ")}
      >
        <p className="font-medium">
          {dragHot ? "Drop to attach" : "Drag files here or click to choose"}
        </p>
        <p className="text-[11px] text-slateline-400 mt-1">
          {ALLOWED_EXTS_DISPLAY}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.txt,.md,.csv,.json,.js,.jsx,.ts,.tsx,.py,.html,.css,.yaml,.yml,.png,.jpg,.jpeg,.webp,.gif,.zip"
        className="hidden"
        onChange={(e) => {
          onFilesPicked(e.target.files);
          // Reset so picking the same filename twice still triggers onChange.
          e.target.value = "";
        }}
      />

      {/* Uploading rows */}
      {uploading.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {uploading.map((u) => (
            <li key={u.tmpId}
                className="flex items-center gap-3 px-3 py-2 rounded-md
                           border border-slateline-200 bg-canvas-card text-sm">
              <span className="flex-1 truncate text-ink">{u.name}</span>
              {u.err
                ? <span className="text-rose-600 text-xs">{u.err}</span>
                : <span className="text-slateline-500 text-xs">{u.pct}%</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Attached rows */}
      {attachments.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {attachments.map((a) => (
            <li key={a.url}
                className="flex items-center gap-3 px-3 py-2 rounded-md
                           border border-slateline-200 bg-canvas-card text-sm">
              <span className="text-brand-green flex-shrink-0" aria-hidden>
                {/* Tiny doc-type indicator */}
                {a.ext === "pdf" ? "📄"
                 : ["png","jpg","jpeg","webp","gif"].includes(a.ext) ? "🖼"
                 : ["js","ts","py","html","css","json","yaml","md"].includes(a.ext) ? "💻"
                 : "📎"}
              </span>
              <span className="flex-1 truncate text-ink">{a.name}</span>
              <span className="text-[11px] text-slateline-400 flex-shrink-0">
                {a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : ""}
              </span>
              <button
                type="button"
                onClick={() => removeAttachment(a.url)}
                aria-label={`Remove ${a.name}`}
                className="text-slateline-400 hover:text-rose-600 text-lg leading-none flex-shrink-0"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StepTurnaround({ turnarounds, selected, onSelect, onBack, onNext }) {
  return (
    <section>
      <div className="mb-6">
        <p className="text-xs text-brand-green tracking-[0.2em] uppercase font-semibold mb-3">
          Step 3
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-ink leading-tight">
          How fast do you need it?
        </h1>
        <p className="text-slateline-600 mt-3 text-sm sm:text-base">
          One flat price per turnaround. No surprises.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {turnarounds.map((t) => {
          const active = selected === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onSelect(t.key)}
              className={[
                "text-left p-5 rounded-md border transition-all",
                active
                  ? "border-ink bg-brand-greenPale/40 ring-1 ring-brand-green/40"
                  : "border-slateline-200 bg-canvas-card hover:border-ink/30",
              ].join(" ")}
            >
              <div className="text-xs text-slateline-500 tracking-wider uppercase mb-1">
                {t.label}
              </div>
              <div className="font-display text-3xl text-ink">
                ${t.price_usd}
              </div>
              <div className="text-sm text-slateline-600 mt-2">
                {t.sub || (t.hours <= 24 ? "Tomorrow."
                  : t.hours <= 48 ? "The default."
                  : "Cheapest.")}
              </div>
            </button>
          );
        })}
      </div>

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue"
      />
    </section>
  );
}

/**
 * StepAccount — the Account step (the 4th of 5).
 *
 * Rebuilt 2026-05-26 PM. Two material changes:
 *
 *   1. Leads with a one-click "Continue with Google" button (the OAuth
 *      path already exists at the backend — see LoginPage.jsx for the
 *      same pattern). Reduces the friction of the most common user
 *      journey to zero clicks beyond "yes, that account, sure".
 *
 *   2. The eyebrow matches the progress bar (Step 4 of 5 now that the
 *      live-session Mode step is gone).
 *
 * The email + password form is preserved verbatim below an "or" divider
 * so users who specifically want a password account still have it.
 *
 * Telemetry: `getverified_account_method_chosen` fires on each path
 * (google / email_register / email_login). If `method='google'`
 * disappears from the cumulative split, our hypothesis was wrong.
 */
function StepAccount({
  authMode, setAuthMode,
  email, setEmail, password, setPassword, name, setName,
  submitting, onSubmit, onBack, priceLabel,
  onMethodChosen,
}) {
  // Build the Google-OAuth redirect URL using the same shape as
  // LoginPage. We want users to come back to /get-verified so the
  // draft restores and they continue the funnel.
  const handleGoogle = () => {
    onMethodChosen?.("google");
    // Side-channel to AuthCallback: tell it where to land us after the
    // OAuth round-trip completes. The backend's Google callback redirects
    // to /auth/callback#token=... regardless of redirect_uri (used only
    // for the Google→backend hop), and AuthCallback's default landing is
    // "/" for non-experts — which strands the user on the landing page
    // when they were mid-wizard. localStorage survives the same-origin
    // round-trip; AuthCallback validates the value before navigating.
    try {
      localStorage.setItem("xpert_oauth_next", "/get-verified");
    } catch { /* storage full / disabled — fall back to default landing */ }
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "https://api.xpert.company";
    const returnTo = window.location.origin + "/get-verified";
    window.location.href =
      `${backendUrl}/api/auth/google?redirect_uri=${encodeURIComponent(returnTo)}`;
  };

  // Wrap the existing email/password submit so we can record which path
  // the user took before delegating to the parent's auth+submit handler.
  const handleEmailSubmit = (e) => {
    onMethodChosen?.(authMode === "login" ? "email_login" : "email_register");
    onSubmit(e);
  };

  return (
    <section>
      <div className="mb-6">
        <p className="text-xs text-brand-green tracking-[0.2em] uppercase font-semibold mb-3">
          Step 4
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-ink leading-tight">
          Last thing — where do we send your verdict?
        </h1>
        <p className="text-slateline-600 mt-3 text-sm sm:text-base">
          We need an account so your expert's signed verdict reaches you, and
          your verification stays in your dashboard.
          {" "}<span className="text-brand-green">${priceLabel}</span> is charged after this step.
        </p>
        {/* Reassurance — addresses the implicit "what are you going to
            do with my email?" worry without belabouring it. */}
        <p className="text-xs text-slateline-500 mt-2">
          No marketing emails. You can delete the account anytime.
        </p>
      </div>

      <div className="max-w-md space-y-4">
        {/* Primary path — one-click Google. */}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={submitting}
          data-testid="getverified-google-btn"
          className="w-full inline-flex items-center justify-center gap-3 h-12 rounded-md
                     border border-slateline-300 bg-white hover:bg-slateline-50
                     disabled:opacity-60 text-ink font-medium text-sm transition-colors"
          aria-label="Continue with Google"
        >
          <GoogleGlyph />
          Continue with Google
        </button>

        {/* Divider — visually demotes the email/password form, which
            historically was the only path. The brand "or" follows the
            same pattern as LoginPage. */}
        <div className="relative py-1" aria-hidden="true">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slateline-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-3 bg-canvas text-slateline-400 uppercase tracking-wider font-semibold">
              or
            </span>
          </div>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-3">
          {authMode === "register" && (
            <div>
              <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
                Your name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                           rounded-md px-4 py-3 text-ink placeholder-slateline-400 outline-none"
                placeholder="Ankit"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                         rounded-md px-4 py-3 text-ink placeholder-slateline-400 outline-none"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
              Password {authMode === "register" && <span className="text-slateline-400">(min 8 chars)</span>}
            </label>
            <input
              type="password"
              required
              minLength={authMode === "register" ? 8 : 1}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 bg-white border border-slateline-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                         rounded-md px-4 py-3 text-ink placeholder-slateline-400 outline-none"
              placeholder="••••••••"
            />
          </div>

          <div className="text-sm text-slateline-600">
            {authMode === "register" ? (
              <>Already have an account?{" "}
                <button type="button" onClick={() => setAuthMode("login")}
                        className="text-brand-green hover:underline">
                  Sign in
                </button>
              </>
            ) : (
              <>Don't have an account yet?{" "}
                <button type="button" onClick={() => setAuthMode("register")}
                        className="text-brand-green hover:underline">
                  Create one
                </button>
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <button type="button" onClick={onBack}
                    className="text-slateline-500 hover:text-ink text-sm">
              ← Back
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-brand-green hover:bg-brand-greenHover disabled:opacity-60
                         text-white font-semibold px-6 py-3 rounded-md text-sm
                         tracking-wide transition-all shadow-sm"
            >
              {submitting
                ? "Submitting…"
                : (authMode === "register"
                    ? "Create account & continue"
                    : "Sign in & continue")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

/**
 * GoogleGlyph — the multi-coloured G mark.
 *
 * Inline SVG rather than the lucide-react icon set (which doesn't carry
 * brand glyphs). Same path data as the LoginPage variant; if we change
 * one, we should change the other. Worth extracting to a shared
 * component once a third surface needs it.
 */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function StepCheckoutPending({ priceLabel }) {
  return (
    <section className="text-center py-16">
      <div className="w-12 h-12 mx-auto border-2 border-brand-green border-t-transparent
                      rounded-full animate-spin mb-6" />
      <h1 className="font-display text-3xl text-ink">
        Sending you to checkout…
      </h1>
      <p className="text-slateline-600 mt-3">
        You'll be charged <span className="text-brand-green">${priceLabel}</span> on Stripe.
        Your expert is already notified.
      </p>
    </section>
  );
}

function StepFooter({ onBack, onNext, nextDisabled, nextLabel = "Continue", hint = null }) {
  return (
    <div className="pt-8">
      {hint && (
        <div className="mb-3 text-right text-xs text-rose-600 font-medium">
          {hint}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="text-slateline-500 hover:text-ink text-sm">
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={nextDisabled}
          className="bg-brand-green hover:bg-brand-greenHover disabled:opacity-40 disabled:cursor-not-allowed
                     text-white font-semibold px-6 py-3 rounded-md text-sm tracking-wide transition-all shadow-sm"
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function labelForCategory(key, categories) {
  return categories.find((c) => c.key === key)?.label || "Verification";
}

function priceLabel(turnaroundKey, turnarounds) {
  return turnarounds.find((t) => t.key === turnaroundKey)?.price_usd ?? 0;
}
